import React, { Fragment } from 'react';
import { useTranslation } from 'react-utilities';
import { TViolation } from '../utils/types';
import {
  isPlatformElementValid,
  isValidatedPlatformEvidence,
  PlatformEvidenceFullyTyped
} from '../utils/platformEvidenceTypes';
import ImageWithSpinner from './ImageWithSpinner';

/**
 * Displays evidence elements as a list of label-info pairs. Currently just
 * displays text elements.
 * Based off of `PlatformEvidenceInfo` in
 * Roblox.ModerationPortal.WebApp/ts/react/pages/ViolationDetails/ItemInfo.tsx
 */
const PlatformEvidenceInfo: React.FC<{ evidence: PlatformEvidenceFullyTyped }> = ({ evidence }) => {
  const { translate } = useTranslation();

  return (
    <Fragment>
      {evidence.elements?.map((elem, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={`elem-${i}`}>
          {elem.labelKey && <dt className='text-secondary'>{translate(elem.labelKey)}</dt>}
          {elem.type === 'image' ? (
            <dd>
              <ImageWithSpinner url={elem.url} altLabelKey={elem.labelKey} />
            </dd>
          ) : (
            <dd>{elem.text}</dd>
          )}
        </Fragment>
      ))}
    </Fragment>
  );
};

export type Props = {
  violation?: TViolation;
};

/**
 * Displays a box with the abuse type along with any evidence elements.
 * Based off of `ItemInfo` in
 * Roblox.ModerationPortal.WebApp/ts/react/pages/ViolationDetails/ItemInfo.tsx
 */
const NotApprovedViolation = ({ violation }: Props): JSX.Element => {
  const { translate } = useTranslation();

  // This check should always pass but is needed to ensure evidence can be treated like PlatformEvidenceFullyTyped
  if (!violation || !violation.evidence.elements) {
    return <React.Fragment />;
  }

  const validElements = violation.evidence.elements?.filter(element => {
    return isPlatformElementValid(element);
  });
  const evidence = {
    ...violation.evidence,
    elements: validElements
  };
  if (!isValidatedPlatformEvidence(evidence)) {
    return <React.Fragment />;
  }

  return (
    <div className='violation-panel'>
      <div className='violation-desc'>
        {violation.abuseTypeTranslationKeys.length > 0 && (
          <Fragment>
            <dt className='text-secondary'>{translate('Label.Violation')}</dt>
            <dd>
              {violation.abuseTypeTranslationKeys
                .map(key => translate(key) ?? translate('Label.AbuseType.Other'))
                .join(', ')}
            </dd>
          </Fragment>
        )}
        <PlatformEvidenceInfo evidence={evidence} />
      </div>
    </div>
  );
};

export default NotApprovedViolation;
